import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";
import type { FormCategoriesListQueryDTO } from "@/modules/forms/domain/contracts/form-categories";
import type {
  CreateFormBodySchema,
  FormsListQueryDTO,
} from "@/modules/forms/domain/contracts/forms";
import { formCategoriesApi, formsApi } from "./client";
import type { FormLotteryPrizeInputDTO, FormLotterySettingsDTO, PatchFormBodyDTO } from "./types";

type CreateFormBodyDTO = z.infer<typeof CreateFormBodySchema>;

export const formsKeys = {
  all: ["forms"] as const,
  list: (q: FormsListQueryDTO) => ["forms", "list", q] as const,
  detail: (id: string) => ["forms", "detail", id] as const,
  categories: {
    all: ["forms", "categories"] as const,
    list: (q: FormCategoriesListQueryDTO) =>
      ["forms", "categories", "list", q] as const,
    lookup: ["forms", "categories", "lookup"] as const,
  },
  responses: (
    formId: string,
    args: { limit: number; offset: number; filt?: string },
  ) => ["forms", formId, "responses", args] as const,
  summary: (formId: string) =>
    ["forms", formId, "responses", "summary"] as const,
};

export function useFormsListQuery(query: FormsListQueryDTO) {
  return useQuery({
    queryKey: formsKeys.list(query),
    queryFn: () => formsApi.list(query),
  });
}

export function useFormDetailQuery(id: string) {
  return useQuery({
    queryKey: formsKeys.detail(id),
    queryFn: () => formsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateFormMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFormBodyDTO) => formsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
    onError: () => toast.error("ສ້າງຟອມບໍ່ສຳເລັດ"),
  });
}

export function usePatchFormMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PatchFormBodyDTO) => formsApi.patch(formId, patch),
    onSuccess: () => {
      toast.success("ບັນທຶກຂໍ້ມູນຟອມສຳເລັດ");
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
    onError: () => toast.error("ບັນທຶກຂໍ້ມູນຟອມບໍ່ສຳເລັດ"),
  });
}

export function usePatchFormStatusMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { status: "draft" | "published" | "closed" }) =>
      formsApi.patchStatus(formId, body),
    onSuccess: () => {
      toast.success("ປ່ຽນສະຖານະຟອມສຳເລັດ");
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
    onError: () => toast.error("ປ່ຽນສະຖານະຟອມບໍ່ສຳເລັດ"),
  });
}

export function useDeleteFormMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => formsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
  });
}

export function useAddFormFieldMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof formsApi.addField>[1]) =>
      formsApi.addField(formId, body),
    onSuccess: () => {
      toast.success("ເພີ່ມຄຳຖາມສຳເລັດ");
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
    },
    onError: () => toast.error("ເພີ່ມຄຳຖາມບໍ່ສຳເລັດ"),
  });
}

export function useUpdateFormFieldMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      fieldId,
      body,
    }: {
      fieldId: string;
      body: Parameters<typeof formsApi.updateField>[2];
    }) => formsApi.updateField(formId, fieldId, body),
    onSuccess: () => {
      toast.success("ອັບເດດຄຳຖາມສຳເລັດ");
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
    },
    onError: () => toast.error("ອັບເດດຄຳຖາມບໍ່ສຳເລັດ"),
  });
}

export function useRemoveFormFieldMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (fieldId: string) => formsApi.removeField(formId, fieldId),
    onSuccess: () => {
      toast.success("ລຶບຄຳຖາມສຳເລັດ");
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
    },
    onError: () => toast.error("ລຶບຄຳຖາມບໍ່ສຳເລັດ"),
  });
}

export function useReorderFormFieldsMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (orderedFieldIds: string[]) =>
      formsApi.reorderFields(formId, { orderedFieldIds }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
    },
    onError: () => toast.error("ປ່ຽນລໍາດັບຟິວບໍ່ສຳເລັດ"),
  });
}

export function useFormResponsesQuery(
  formId: string,
  args: { limit: number; offset: number; filt?: string },
) {
  return useQuery({
    queryKey: formsKeys.responses(formId, args),
    queryFn: () => formsApi.listResponses(formId, args),
    enabled: !!formId,
  });
}

export function useFormCategoriesListQuery(query: FormCategoriesListQueryDTO) {
  return useQuery({
    queryKey: formsKeys.categories.list(query),
    queryFn: () => formCategoriesApi.list(query),
  });
}

export function useFormCategoriesLookupQuery() {
  return useQuery({
    queryKey: formsKeys.categories.lookup,
    queryFn: () => formCategoriesApi.lookup({ limit: 100 }),
  });
}

export function useCreateFormCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof formCategoriesApi.create>[0]) =>
      formCategoriesApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formsKeys.categories.all });
    },
  });
}

export function usePatchFormCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: Parameters<typeof formCategoriesApi.patch>[1];
    }) => formCategoriesApi.patch(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formsKeys.categories.all });
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
  });
}

export function useDeleteFormCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => formCategoriesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: formsKeys.categories.all });
      qc.invalidateQueries({ queryKey: formsKeys.all });
    },
  });
}

export function useFormResponsesSummaryQuery(
  formId: string,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: formsKeys.summary(formId),
    queryFn: () => formsApi.getResponsesSummary(formId),
    enabled: !!formId && (options?.enabled ?? true),
  });
}

export function useSubmitAdminFormResponseMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof formsApi.submitAdminResponse>[1]) =>
      formsApi.submitAdminResponse(formId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forms", formId, "responses"] });
      qc.invalidateQueries({ queryKey: ["form-responses", formId] });
      qc.invalidateQueries({ queryKey: formsKeys.summary(formId) });
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
    },
  });
}

export const formLotteryKeys = {
  detail: (formId: string) => ["forms", formId, "lottery"] as const,
  entries: (formId: string, q: { limit: number; offset: number; search?: string }) =>
    ["forms", formId, "lottery", "entries", q] as const,
};

export function useFormLotteryQuery(formId: string) {
  return useQuery({
    queryKey: formLotteryKeys.detail(formId),
    queryFn: () => formsApi.getLottery(formId),
    enabled: !!formId,
  });
}

export function useFormLotteryEntriesQuery(
  formId: string,
  query: { limit: number; offset: number; search?: string },
) {
  return useQuery({
    queryKey: formLotteryKeys.entries(formId, query),
    queryFn: () => formsApi.listLotteryEntries(formId, query),
    enabled: !!formId,
  });
}

function invalidateLottery(qc: ReturnType<typeof useQueryClient>, formId: string) {
  qc.invalidateQueries({ queryKey: formLotteryKeys.detail(formId) });
  qc.invalidateQueries({ queryKey: ["forms", formId, "lottery", "entries"] });
}

export function useSaveFormLotterySettingsMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FormLotterySettingsDTO) =>
      formsApi.saveLotterySettings(formId, body),
    onSuccess: () => {
      toast.success("ບັນທຶກການຕັ້ງຄ່າລຸ້ນລາງວັນແລ້ວ");
      invalidateLottery(qc, formId);
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
    },
    onError: () => toast.error("ບັນທຶກການຕັ້ງຄ່າລຸ້ນລາງວັນບໍ່ສຳເລັດ"),
  });
}

export function useCreateFormLotteryPrizeMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: FormLotteryPrizeInputDTO) =>
      formsApi.createLotteryPrize(formId, body),
    onSuccess: () => {
      toast.success("ເພີ່ມລາງວັນແລ້ວ");
      invalidateLottery(qc, formId);
    },
    onError: () => toast.error("ເພີ່ມລາງວັນບໍ່ສຳເລັດ"),
  });
}

export function usePatchFormLotteryPrizeMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      prizeId,
      body,
    }: {
      prizeId: string;
      body: Partial<FormLotteryPrizeInputDTO>;
    }) => formsApi.patchLotteryPrize(formId, prizeId, body),
    onSuccess: () => {
      toast.success("ບັນທຶກລາງວັນແລ້ວ");
      invalidateLottery(qc, formId);
    },
    onError: (err: unknown) => {
      const code =
        err !== null &&
        typeof err === "object" &&
        "code" in err &&
        typeof (err as { code: unknown }).code === "string"
          ? (err as { code: string }).code
          : "";
      if (code === "QUANTITY_BELOW_CLAIMED") {
        toast.error("ຈຳນວນລວມນ້ອຍກວ່າທີ່ແຈ້ງແລ້ວ");
        return;
      }
      toast.error("ບັນທຶກລາງວັນບໍ່ສຳເລັດ");
    },
  });
}

export function useDrawFormLotteryPrizeMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { prizeId: string; count?: number }) =>
      formsApi.drawLotteryPrize(formId, args.prizeId, args.count),
    onSuccess: (result) => {
      toast.success(`ສຸ່ມລາງວັນໄດ້ ${result.drawn} ຄົນ`);
      invalidateLottery(qc, formId);
    },
    onError: () => toast.error("ສຸ່ມລາງວັນ (ໂໝດຫຼັງ) ບໍ່ສຳເລັດ"),
  });
}

export function useRedeemFormLotteryEntryMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => formsApi.redeemLotteryEntry(formId, entryId),
    onSuccess: () => {
      toast.success("ຢືນຢັນການຮັບລາງວັນແລ້ວ");
      invalidateLottery(qc, formId);
    },
    onError: () => toast.error("ຢືນຢັນການຮັບລາງວັນບໍ່ສຳເລັດ"),
  });
}

export function useDeleteFormResponseMutation(formId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (responseId: string) =>
      formsApi.deleteResponse(formId, responseId),
    onSuccess: () => {
      toast.success("ລຶບຄຳຕອບສຳເລັດ");
      qc.invalidateQueries({ queryKey: ["forms", formId, "responses"] });
      qc.invalidateQueries({ queryKey: ["form-responses", formId] });
      qc.invalidateQueries({ queryKey: formsKeys.summary(formId) });
      qc.invalidateQueries({ queryKey: formsKeys.detail(formId) });
    },
    onError: () => toast.error("ລຶບຄຳຕອບບໍ່ສຳເລັດ"),
  });
}
